import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import Isvg from 'react-inlinesvg';

import { VenueUserFeedbackOptionType } from '@classpass/protobuf-definitions/lib/web/models/partners/reservation';
import classNames from 'classnames';

import { bem } from 'helpers/class-name';
import Segment from 'helpers/segment';

import { ICONS } from 'constants/icons';
import { intlMessage } from 'constants/propTypes';
import Button from 'components/Button';
import Callout from 'components/Callout';
import { CardSection } from 'components/Card';
import { openDecagonChat } from 'components/Decagon';
import { Form } from 'components/HookForm';
import { closeModal } from 'components/Modal';
import Message from 'components/Message';
import When from 'components/When';
import { selectIsMobile } from 'selectors/ui';

import {
  PROCESSING_MIN_DISPLAY_MS,
  REPORT_TYPE_DISRUPTIVE,
  REPORT_TYPES,
  REPORT_TYPE_TRIAL,
} from '../ReservationFeedbackModalV2.constants';
import GuidancePanel from './GuidancePanel';
import ReportFormPanel from './ReportFormPanel';
import ReportDetailsHeader from './ReportDetailsHeader';

import './ReportDetails.scss';
import messages from '../ReservationFeedbackModalV2.messages';

const { block, element } = bem('report-details');
const FORM_NAME = 'reservation-feedback-report-form';

const EMPTY_FORM_VALUES = { additionalDetails: '', disruptiveReason: null };

function buildFeedbackAndDescription(formValues, reportType, disruptiveReasons) {
  const description = (formValues?.additionalDetails ?? '').trim();
  let feedback;
  if (reportType === REPORT_TYPE_TRIAL) {
    feedback = [VenueUserFeedbackOptionType.TRIAL_ABUSE];
  } else if (reportType === REPORT_TYPE_DISRUPTIVE) {
    const reason = formValues?.disruptiveReason;
    const reasonValue = disruptiveReasons.find(({ message }) => message.id === reason?.id)?.value;
    feedback = [VenueUserFeedbackOptionType.DISRUPTIVE_USER, reasonValue].filter(Boolean);
  } else {
    feedback = [];
  }
  return { description, feedback };
}

const ReportDetails = ({
  fullName,
  profileUrl,
  reasonType,
  guidelines,
  venueId,
  reservationId,
  disruptiveReasons = [],
  submitReport,
  onReported,
  reportType,
  setProcessingStep,
  setSuccessStep,
  resizeModal,
  failedFormValues,
  onSubmitError,
}) => {
  const dispatch = useDispatch();
  const isMobile = useSelector(selectIsMobile);
  const [showGuidancePanel, setShowGuidancePanel] = useState(false);

  useEffect(() => {
    if (resizeModal && typeof resizeModal === 'function') {
      resizeModal();
    }
  }, [showGuidancePanel, resizeModal]);

  const toggleShowGuidancePanel = () => {
    setShowGuidancePanel(!showGuidancePanel);
  };

  const handleClose = () => {
    Segment.event('Report click cancel', { VID: venueId, RID: reservationId });
    dispatch(closeModal());
  };

  const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

  async function onSubmitReport(formValues) {
    Segment.event('Click Submit Report', {
      VID: venueId,
      ReservationID: reservationId,
      Type: reportType,
    });
    setProcessingStep();
    const minDelayPromise = wait(PROCESSING_MIN_DISPLAY_MS);
    const { feedback, description } = buildFeedbackAndDescription(formValues, reportType, disruptiveReasons);
    try {
      const submitPromise = submitReport({ feedback, description });
      const response = await Promise.all([minDelayPromise, submitPromise]).then(([, result]) => result);
      onReported?.();
      setSuccessStep(response.isPaid);
    } catch (error) {
      onSubmitError(formValues);
    }
  }

  return (
    <>
      <When predicate={isMobile}>
        <CardSection flush className={element('mobile-header')}>
          <ReportDetailsHeader fullName={fullName} profileUrl={profileUrl} isMobile={isMobile} />
        </CardSection>
      </When>
      <CardSection flush className={element('content')}>
        <div className={classNames(block(), 'reservation-feedback-modal-v2__step2-wrapper')}>
          <div className={element('report-badge-wrapper')}>
            <GuidancePanel
              reasonType={reasonType}
              guidelines={guidelines}
              isMobile={isMobile}
              isOpen={showGuidancePanel}
              onToggle={toggleShowGuidancePanel}
              venueId={venueId}
              reservationId={reservationId}
            />
            <div className={element('report-section')}>
              <When predicate={!isMobile}>
                <ReportDetailsHeader fullName={fullName} profileUrl={profileUrl} isMobile={isMobile} />
              </When>
              <When predicate={!!failedFormValues}>
                <div className={element('submit-error-wrapper')}>
                  <Callout className={element('submit-error')} icon={<Isvg src={ICONS.alert} />} variant="error">
                    <Message
                      message={messages.submitError}
                      values={{
                        contactUs: text => (
                          <Button onClick={openDecagonChat} styled="inline-link" type={Button.TYPES.BUTTON}>
                            {text}
                          </Button>
                        ),
                      }}
                    />
                  </Callout>
                </div>
              </When>
              <Form
                id={FORM_NAME}
                className={element('report-form')}
                defaultValues={failedFormValues ?? EMPTY_FORM_VALUES}
                onSubmit={() => () => Promise.resolve()}>
                {({ handleSubmit }) => (
                  <ReportFormPanel
                    fullName={fullName}
                    profileUrl={profileUrl}
                    reasonType={reasonType}
                    disruptiveReasons={disruptiveReasons.map(({ message }) => message)}
                    isMobile={isMobile}
                    handleClose={handleClose}
                    handleSubmit={handleSubmit}
                    onSubmitReport={onSubmitReport}
                    formId={FORM_NAME}
                  />
                )}
              </Form>
            </div>
          </div>
        </div>
      </CardSection>
    </>
  );
};

ReportDetails.propTypes = {
  reasonType: intlMessage.isRequired,
  guidelines: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.oneOfType([intlMessage, PropTypes.arrayOf(intlMessage)]).isRequired,
      descriptionFooter: intlMessage,
      icon: PropTypes.string.isRequired,
      title: intlMessage.isRequired,
    })
  ).isRequired,
  fullName: PropTypes.string.isRequired,
  profileUrl: PropTypes.string,
  venueId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  reservationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disruptiveReasons: PropTypes.arrayOf(
    PropTypes.shape({ message: intlMessage.isRequired, value: PropTypes.number.isRequired }).isRequired
  ),
  submitReport: PropTypes.func.isRequired,
  onReported: PropTypes.func,
  reportType: PropTypes.oneOf(REPORT_TYPES).isRequired,
  setProcessingStep: PropTypes.func.isRequired,
  setSuccessStep: PropTypes.func.isRequired,
  resizeModal: PropTypes.func,
  failedFormValues: PropTypes.shape({
    additionalDetails: PropTypes.string,
    disruptiveReason: intlMessage,
  }),
  onSubmitError: PropTypes.func,
};

export default ReportDetails;
